import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Plus, Hash, X, Search, Edit2 } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import TablePager, { usePagedRows } from '../TablePager';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import { formatCurrency, formatDate, formatSignedAmount, TypeBadge, PaymentBadge, LoadingRow, errorText } from './shared';

// Every cheque number the ledger has seen, one row per cheque and bank: what was drawn on it and
// what was paid out of it. A cheque cashed for the month shows the expenses paid from that cash
// as they are entered against its number; a cheque written straight to a vendor shows the
// withdrawal and the expense of the same amount.

const Remaining = ({ cheque }) => {
  const remaining = Number(cheque.remaining || 0);
  const withdrawn = Number(cheque.withdrawn || 0);
  if (withdrawn === 0) return <span className="text-xs text-gray-400">No withdrawal recorded</span>;
  if (remaining === 0) return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">Fully used</span>;
  if (remaining < 0) return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Over by {formatCurrency(-remaining)}</span>;
  return <span className="text-sm font-semibold text-emerald-700">{formatCurrency(remaining)} left</span>;
};

const ExpenseCheques = ({ refreshKey, can, onRecordWithdrawal, onEdit }) => {
  const showToast = useToast();
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await expenseAPI.getCheques();
      setCheques(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast('error', errorText(err, 'Failed to load cheques'));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openCheque = useCallback(async (chq) => {
    setSelected(chq);
    setEntriesLoading(true);
    try {
      const data = await expenseAPI.getChequeEntries(chq.chequeNumber, chq.bank || undefined);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast('error', errorText(err, 'Failed to load the cheque'));
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, [showToast]);

  // Keep the open cheque's figures and lines fresh after a save elsewhere.
  useEffect(() => {
    if (!selected) return;
    const fresh = cheques.find(c => c.chequeNumber === selected.chequeNumber && (c.bank || '') === (selected.bank || ''));
    if (fresh && fresh !== selected) openCheque(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cheques]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cheques;
    return cheques.filter(c => (c.chequeNumber || '').toLowerCase().includes(q) || (c.bank || '').toLowerCase().includes(q));
  }, [cheques, search]);

  const paging = usePagedRows(filtered, { id: 'expense-cheques', resetOn: [search] });
  const entriesPaging = usePagedRows(entries, { id: 'expense-cheque-entries', resetOn: [selected?.chequeNumber, selected?.bank] });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-600" /> Cheques
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cheque number or bank"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none w-56"
              />
            </div>
            {can('RECORD_PAYMENT') && (
              <button onClick={onRecordWithdrawal} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium">
                <Plus className="h-4 w-4" /> Record a withdrawal
              </button>
            )}
          </div>
        </div>

        {loading && cheques.length === 0 ? (
          <LoadingRow text="Loading cheques..." />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <CreditCard className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No cheques</p>
            <p className="text-gray-400 text-sm mt-1">{search ? 'Nothing matches that number' : 'Cheques appear here as withdrawals and cheque expenses are entered'}</p>
          </div>
        ) : (
          <>
            <TablePager paging={paging} noun="cheque" className="px-4" rounded={false} />
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cheque</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Bank</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">First seen</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last seen</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Withdrawn</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Spent</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Remaining</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Entries</th>
                </tr>
              </thead>
              <tbody>
                {paging.pageRows.map((chq) => {
                  const isOpen = selected && selected.chequeNumber === chq.chequeNumber && (selected.bank || '') === (chq.bank || '');
                  return (
                    <tr
                      key={`${chq.chequeNumber}|${chq.bank || ''}`}
                      tabIndex={0}
                      onClick={() => openCheque(chq)}
                      onKeyDown={onActivateKey(() => openCheque(chq))}
                      aria-selected={!!isOpen}
                      className={`border-b border-gray-50 transition-colors cursor-pointer ${isOpen ? 'bg-navy-50' : 'hover:bg-gray-50'} ${ROW_FOCUS_RING}`}
                    >
                      <td className="px-4 py-3 text-sm font-mono font-semibold text-gray-900">{chq.chequeNumber}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{chq.bank || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(chq.firstDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(chq.lastDate)}</td>
                      <td className="px-4 py-3 text-sm text-blue-700 font-semibold text-right">{formatCurrency(chq.withdrawn)}</td>
                      <td className="px-4 py-3 text-sm text-red-700 font-semibold text-right">{formatCurrency(chq.spent)}</td>
                      <td className="px-4 py-3 text-right"><Remaining cheque={chq} /></td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {chq.withdrawalCount} in · {chq.expenseCount} out
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePager paging={paging} noun="cheque" position="bottom" className="px-4" />
          </>
        )}
      </div>

      {selected && (
        <div className="bg-white rounded-2xl shadow-xl">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Hash className="h-5 w-5 text-emerald-600" /> Cheque {selected.chequeNumber}{selected.bank ? ` · ${selected.bank}` : ''}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Withdrawn {formatCurrency(selected.withdrawn)}, spent {formatCurrency(selected.spent)}, {formatCurrency(selected.remaining)} remaining
              </p>
            </div>
            <button aria-label="Close" onClick={() => { setSelected(null); setEntries([]); }} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          {entriesLoading ? (
            <LoadingRow />
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No entries on this cheque</div>
          ) : (
            <>
              <TablePager paging={entriesPaging} noun="entry" nounPlural="entries" className="px-4" rounded={false} />
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Paid by</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    {can('VIEW_ENTRIES_TAB') && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {entriesPaging.pageRows.map((entry) => (
                    <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(entry.entryDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {entry.description}
                        <div className="mt-0.5"><TypeBadge type={entry.entryType} /></div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{entry.categoryPath || '-'}</td>
                      <td className="px-4 py-3"><PaymentBadge mode={entry.paymentMode} /></td>
                      <td className={`px-4 py-3 text-sm font-semibold text-right whitespace-nowrap ${entry.entryType === 'WITHDRAWAL' ? 'text-blue-700' : 'text-red-700'}`}>
                        {formatSignedAmount(entry)}
                      </td>
                      {can('VIEW_ENTRIES_TAB') && (
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => onEdit(entry)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Edit">
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePager paging={entriesPaging} noun="entry" nounPlural="entries" position="bottom" className="px-4" />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseCheques;
